import type { TokenId } from "@/types/token.types";

declare global {
    interface Window {
        safary?: {
            track: (args: {
                eventType: string;
                eventName: string;
                parameters?: { [key: string]: string | number };
            }) => void;
        };
    }
}

/**
 * Basic track function that directly calls the Safary SDK
 */
export const safaryTrack = (args: {
    eventType: string;
    eventName: string;
    parameters?: { [key: string]: string | number };
}) => {
    if (typeof window !== "undefined" && window.safary) {
        try {
            window.safary.track(args);
        } catch (error) {
            console.error("Safary tracking error:", error);
        }
    }
};

/**
 * Parameters for tracking a successful deposit event
 */
interface SafaryTrackDepositSuccessParams {
    /** The token that was deposited to */
    tokenOut: TokenId;
    /** Amount of tokens received */
    tokenAmountOut: number;
    /** The token that was deposited from */
    tokenIn: TokenId;
    /** Amount of tokens deposited */
    tokenAmountIn: number;
    /** User's wallet address */
    walletAddress: string;
}

/**
 * Tracks a successful deposit event
 * @param params Object containing deposit success tracking parameters
 */
export const safaryTrackDepositSuccess = (params: SafaryTrackDepositSuccessParams) => {
    safaryTrack({
        eventName: "depositSuccess",
        eventType: "custom",
        parameters: {
            customStr1Label: "tokenOut",
            customStr1Value: params.tokenOut,
            customNr1Label: "tokenAmountOut",
            customNr1Value: params.tokenAmountOut,
            customStr2Label: "tokenIn",
            customStr2Value: params.tokenIn,
            customNr2Label: "tokenAmountIn",
            customNr2Value: params.tokenAmountIn,
            customStr3Label: "walletAddress",
            customStr3Value: params.walletAddress,
        },
    });
};

/**
 * Parameters for tracking a successful mint event
 */
interface SafaryTrackMintSuccessParams {
    /** User's wallet address */
    walletAddress: string;
    /** The token that was used to mint */
    tokenIn: TokenId;
    /** The token that was minted */
    tokenOut: TokenId;
    /** Amount of iUSD minted */
    iUsdMintedAmount: number;
}

/**
 * Tracks a successful mint event
 * @param params Object containing mint success tracking parameters
 */
export const safaryTrackMintSuccess = (params: SafaryTrackMintSuccessParams) => {
    safaryTrack({
        eventName: "iUsdMintSuccess",
        eventType: "custom",
        parameters: {
            customStr1Label: "address",
            customStr1Value: params.walletAddress,
            customStr2Label: "tokenIn",
            customStr2Value: params.tokenIn,
            customStr3Label: "tokenOut",
            customStr3Value: params.tokenOut,
            customNr1Label: "iUsdMintedAmount",
            customNr1Value: params.iUsdMintedAmount,
        },
    });
};

/**
 * Parameters for tracking a successful lock event
 */
interface SafaryTrackLockSuccessParams {
    /** Duration of the lock in seconds */
    duration: number;
    /** The token that was locked */
    tokenIn: TokenId;
    /** Amount of tokens locked */
    tokenAmountIn: number;
    /** User's wallet address */
    walletAddress: string;
}

/**
 * Tracks a successful lock event
 * @param params Object containing lock success tracking parameters
 */
export const safaryTrackLockSuccess = (params: SafaryTrackLockSuccessParams) => {
    safaryTrack({
        eventName: "lockSuccess",
        eventType: "custom",
        parameters: {
            customNr1Label: "duration",
            customNr1Value: params.duration,
            customStr1Label: "tokenIn",
            customStr1Value: params.tokenIn,
            customNr2Label: "tokenAmountIn",
            customNr2Value: params.tokenAmountIn,
            customStr2Label: "walletAddress",
            customStr2Value: params.walletAddress,
        },
    });
};

/**
 * Parameters for tracking a points task link click event
 */
interface SafaryTrackPointsTaskLinkClickParams {
    /** User's total value locked */
    userTvl: number;
    /** User's wallet address */
    walletAddress: string;
    /** Destination URL or path */
    destination: string;
    /** Unique identifier for the task */
    taskId: string;
    /** Display name of the task */
    taskDisplayName: string;
}

/**
 * Tracks a points task link click event
 * @param params Object containing points task link click tracking parameters
 */
export const safaryTrackPointsTaskLinkClick = (params: SafaryTrackPointsTaskLinkClickParams) => {
    safaryTrack({
        eventName: "pointsTaskLinkClick",
        eventType: "custom",
        parameters: {
            customNr1Label: "userTvl",
            customNr1Value: params.userTvl,
            customStr1Label: "walletAddress",
            customStr1Value: params.walletAddress,
            customStr2Label: "destination",
            customStr2Value: params.destination,
            customStr3Label: "taskId",
            customStr3Value: params.taskId,
            customStr4Label: "taskDisplayName",
            customStr4Value: params.taskDisplayName,
        },
    });
};

/**
 * Parameters for tracking a successful start exit event
 */
interface SafaryTrackStartExitSuccessParams {
    /** Amount of tokens being withdrawn */
    tokenAmountOut: number;
    /** Amount of tokens locked in the bucket */
    tokenAmountLockedInBucket: number;
    /** Duration of the lock period */
    duration: number;
    /** User's wallet address */
    walletAddress: string;
}

/**
 * Tracks a successful start exit event
 * @param params Object containing start exit success tracking parameters
 */
export const safaryTrackStartExitSuccess = (params: SafaryTrackStartExitSuccessParams) => {
    safaryTrack({
        eventName: "startExitSuccess",
        eventType: "custom",
        parameters: {
            customNr1Label: "tokenAmountOut",
            customNr1Value: params.tokenAmountOut,
            customNr2Label: "tokenAmountLockedInBucket",
            customNr2Value: params.tokenAmountLockedInBucket,
            customNr3Label: "duration",
            customNr3Value: params.duration,
            customStr1Label: "walletAddress",
            customStr1Value: params.walletAddress,
        },
    });
};

/**
 * Parameters for tracking a successful vote event
 */
interface SafaryTrackVoteSuccessParams {
    /** Epoch number for the vote */
    epoch: number;
    /** User's wallet address */
    walletAddress: string;
}

/**
 * Tracks a successful vote event
 * @param params Object containing vote success tracking parameters
 */
export const safaryTrackVoteSuccess = (params: SafaryTrackVoteSuccessParams) => {
    safaryTrack({
        eventName: "voteSuccess",
        eventType: "custom",
        parameters: {
            customNr1Label: "epoch",
            customNr1Value: params.epoch,
            customStr1Label: "walletAddress",
            customStr1Value: params.walletAddress,
        },
    });
};

/**
 * Parameters for tracking when the whale dialog is opened
 */
interface SafaryTrackWhaleDialogOpenParams {
    /** User's total value locked */
    userTvl: number;
    /** User's wallet address */
    walletAddress: string;
}

/**
 * Tracks when the whale dialog is opened
 * @param params Object containing whale dialog open tracking parameters
 */
export const safaryTrackWhaleDialogOpen = (params: SafaryTrackWhaleDialogOpenParams) => {
    safaryTrack({
        eventName: "whaleDialogOpened",
        eventType: "custom",
        parameters: {
            customNr1Label: "userTvl",
            customNr1Value: params.userTvl,
            customStr1Label: "walletAddress",
            customStr1Value: params.walletAddress,
        },
    });
};

/**
 * Parameters for tracking when the telegram link in the whale dialog is clicked
 */
interface SafaryTrackWhaleDialogTelegramLinkClickParams {
    /** User's total value locked */
    userTvl: number;
    /** Whale's wallet address */
    whaleAddress: string;
}

/**
 * Tracks when the telegram link in the whale dialog is clicked
 * @param params Object containing whale dialog telegram click tracking parameters
 */
export const safaryTrackWhaleDialogTelegramLinkClick = (params: SafaryTrackWhaleDialogTelegramLinkClickParams) => {
    safaryTrack({
        eventName: "whaleDialogTelegramClicked",
        eventType: "custom",
        parameters: {
            customNr1Label: "userTvl",
            customNr1Value: params.userTvl,
            customStr1Label: "whaleAddress",
            customStr1Value: params.whaleAddress,
        },
    });
};

// Backward compatibility layer - this will be tree-shaken if not used
// and can be removed once all imports are updated
export const SafaryService = {
    deposit: {
        success: safaryTrackDepositSuccess,
    },
    mint: {
        success: safaryTrackMintSuccess,
    },
    lock: {
        success: safaryTrackLockSuccess,
    },
    pointsTask: {
        link: {
            click: safaryTrackPointsTaskLinkClick,
        },
    },
    startExit: {
        success: safaryTrackStartExitSuccess,
    },
    vote: {
        success: safaryTrackVoteSuccess,
    },
    whaleDialog: {
        open: safaryTrackWhaleDialogOpen,
        telegramLinkClick: safaryTrackWhaleDialogTelegramLinkClick,
    },
};
